const LANGUAGE_CODES_BY_NAME = {
  English: "en",
  Spanish: "es",
  "Simplified Chinese": "zh-Hans",
  Tagalog: "tl",
  Vietnamese: "vi",
  Arabic: "ar",
  French: "fr",
  Korean: "ko",
  Russian: "ru",
  Portuguese: "pt",
  "Haitian Creole": "ht",
  Hindi: "hi",
  Persian: "fa",
  Icelandic: "is",
  Japanese: "ja",
};

const LANGUAGE_NAMES_BY_CODE = Object.entries(LANGUAGE_CODES_BY_NAME).reduce(
  (names, [language, languageCode]) => {
    names[languageCode.toLowerCase()] = language;
    return names;
  },
  {}
);

function getTrimmedString(value) {
  return typeof value === "string" ? value.trim() : "";
}

export function normalizeLanguageCode(value) {
  const code = getTrimmedString(value).replace(/_/g, "-");

  if (!code) {
    return "";
  }

  return code
    .split("-")
    .map((part, index) => {
      if (index === 0) return part.toLowerCase();
      if (part.length === 4) {
        return `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`;
      }
      if (part.length === 2 || /^\d{3}$/.test(part)) {
        return part.toUpperCase();
      }
      return part.toLowerCase();
    })
    .join("-");
}

function getLanguageCodeFromName(language) {
  const cleanLanguage = getTrimmedString(language);

  if (!cleanLanguage) {
    return "";
  }

  return LANGUAGE_CODES_BY_NAME[cleanLanguage] || "";
}

function getLanguageNameFromCode(languageCode) {
  const normalizedCode = normalizeLanguageCode(languageCode);

  if (!normalizedCode) {
    return "";
  }

  return (
    LANGUAGE_NAMES_BY_CODE[normalizedCode.toLowerCase()] ||
    LANGUAGE_NAMES_BY_CODE[normalizedCode.split("-")[0]] ||
    ""
  );
}

export function getLanguageIdentityKey(languageCode, language = "") {
  const normalizedCode = normalizeLanguageCode(languageCode);
  const code = normalizedCode || getLanguageCodeFromName(language);

  if (!code) {
    return "";
  }

  const [baseLanguage, ...subtags] = code.split("-");

  if (baseLanguage === "fil" || baseLanguage === "tl") {
    return "tl";
  }

  if (baseLanguage === "zh") {
    const script = subtags.find((subtag) => subtag.length === 4);
    return script ? `zh-${script}` : "zh";
  }

  return baseLanguage;
}

export function normalizeLanguageMetadata(
  metadata,
  fallback = { language: "English", languageCode: "en" }
) {
  const language = getTrimmedString(metadata?.language) ||
    getTrimmedString(metadata?.name);
  const languageCode = normalizeLanguageCode(
    metadata?.languageCode || metadata?.code
  );
  const fallbackLanguage = getTrimmedString(fallback?.language);
  const fallbackLanguageCode = normalizeLanguageCode(fallback?.languageCode);
  const resolvedLanguageCode =
    languageCode || getLanguageCodeFromName(language) || fallbackLanguageCode;
  const resolvedLanguage =
    language || getLanguageNameFromCode(resolvedLanguageCode) || fallbackLanguage;

  return {
    language: resolvedLanguage,
    languageCode: resolvedLanguageCode,
    identityKey: getLanguageIdentityKey(resolvedLanguageCode, resolvedLanguage),
  };
}

export function normalizeTranslationLanguages(translationLanguages) {
  if (!Array.isArray(translationLanguages)) {
    return [];
  }

  return translationLanguages.reduce((languages, entry) => {
    const metadata =
      typeof entry === "string"
        ? normalizeLanguageMetadata({ language: entry }, {})
        : normalizeLanguageMetadata(entry, {});

    if (!metadata.language && !metadata.languageCode) {
      return languages;
    }

    languages.push({
      language: metadata.language,
      languageCode: metadata.languageCode,
    });
    return languages;
  }, []);
}

function getPrimaryLanguageMetadata(card, generation) {
  if (card?.primaryLanguage || card?.primaryLanguageCode) {
    return normalizeLanguageMetadata({
      language: card.primaryLanguage,
      languageCode: card.primaryLanguageCode,
    });
  }

  if (generation?.primaryLanguage || generation?.primaryLanguageCode) {
    return normalizeLanguageMetadata({
      language: generation.primaryLanguage,
      languageCode: generation.primaryLanguageCode,
    });
  }

  return normalizeLanguageMetadata();
}

function getTranslationCandidates(card, translationLanguage = "") {
  const candidates = Array.isArray(card?.translations)
    ? card.translations
    : [];

  if (candidates.length > 0) {
    return candidates;
  }

  const text = getTrimmedString(card?.translation);

  if (!text) {
    return [];
  }

  return [
    {
      language: card?.translationLanguage || translationLanguage,
      languageCode: card?.translationLanguageCode,
      text,
    },
  ];
}

export function getDistinctCardTranslation(
  card,
  {
    generation = null,
    translationLanguage = "",
    primaryQuestion = "",
  } = {}
) {
  return getDistinctCardTranslations(card, {
    generation,
    translationLanguage,
    primaryQuestion,
  })[0] || {
    text: "",
    language: "",
    languageCode: "",
  };
}

export function getDistinctCardTranslations(
  card,
  {
    generation = null,
    translationLanguage = "",
    primaryQuestion = "",
    maxTranslations = 2,
  } = {}
) {
  const primaryMetadata = getPrimaryLanguageMetadata(card, generation);
  const primaryIdentity = primaryMetadata.identityKey;
  const question = getTrimmedString(
    primaryQuestion || card?.questionRefined || card?.question || card?.refinedQuestion
  );
  const fallbackMetadata = translationLanguage
    ? normalizeLanguageMetadata({ language: translationLanguage })
    : {};
  const selectedTexts = new Set();
  const selectedIdentities = new Set();

  return getTranslationCandidates(card, translationLanguage).reduce(
    (translations, candidate) => {
      if (translations.length >= maxTranslations) {
        return translations;
      }

      const text = getTrimmedString(candidate?.text);

      if (!text || text === question || selectedTexts.has(text)) {
        return translations;
      }

      const metadata = normalizeLanguageMetadata(candidate, fallbackMetadata);
      const knownMetadata = normalizeLanguageMetadata(candidate, {});
      const identity = knownMetadata.identityKey;

      if (
        identity &&
        primaryIdentity &&
        identity === primaryIdentity
      ) {
        return translations;
      }

      if (identity && selectedIdentities.has(identity)) {
        return translations;
      }

      translations.push({
        text,
        language: metadata.language,
        languageCode: metadata.languageCode,
      });
      selectedTexts.add(text);

      if (identity) {
        selectedIdentities.add(identity);
      }

      return translations;
    },
    []
  );
}

export function getCardLanguagePresentation(
  card,
  { generation = null, translationLanguage = "" } = {}
) {
  const question = getTrimmedString(
    card?.questionRefined || card?.question || card?.refinedQuestion
  );
  const primaryMetadata = getPrimaryLanguageMetadata(card, generation);
  const translations = getDistinctCardTranslations(card, {
    generation,
    translationLanguage,
    primaryQuestion: question,
  });
  const translation = translations[0] || {
    text: "",
    language: "",
    languageCode: "",
  };

  return {
    question,
    primaryLanguage: primaryMetadata.language,
    primaryLanguageCode: primaryMetadata.languageCode,
    translations,
    translation: translation.text,
    translationLanguage: translation.language,
    translationLanguageCode: translation.languageCode,
  };
}

export function getFirstDistinctTranslationText(
  translations,
  {
    primaryLanguage = "English",
    primaryLanguageCode = "en",
    primaryQuestion = "",
  } = {}
) {
  const presentation = getCardLanguagePresentation({
    questionRefined: primaryQuestion,
    primaryLanguage,
    primaryLanguageCode,
    translations,
  });

  return presentation.translation;
}
